import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export default function ResultCard({
  item = {},
  city: cityProp,
  region: regionProp,
  // ✅ new: when true, card stretches to full width of its grid column
  fullWidth = false,
}) {
  const {
    id,
    title = "Downtown",
    rating = 5.0,
    reviews = "6.2k",
    subtitle = "Vibrant city center with nightlife",
    image = "",
    fallbackImage = "",   // ✅ 2026-05-06 (Tier 2.3): swap-on-error src (e.g. neighborhood heroImage)
    ctaUrl = "",          // may contain Google Maps URL
    ctaText,              // ✅ optional custom label (e.g., "Location")
    city: itemCity,
    state: itemState,
    region: itemRegion,
  } = item || {};

  const navigate = useNavigate();
  const location = useLocation(); // 👈 detect current page
  // Two-stage image error model:
  //   imgStage 0 → trying primary `image`
  //   imgStage 1 → tried primary, failed, now trying `fallbackImage`
  //   imgStage 2 → fallback also failed (or absent), show "No image"
  const [imgStage, setImgStage] = useState(0);
  // Reset error cascade when the parent passes a new primary src — e.g.
  // ExploreMore swapping POI categories, where the same React slot gets
  // reused but the image URL changes. Without this, a card stuck at
  // stage 2 ("No image") would never recover.
  useEffect(() => {
    setImgStage(0);
  }, [image, fallbackImage]);
  const currentSrc =
    imgStage === 0 ? image : (imgStage === 1 && fallbackImage ? fallbackImage : "");

  const stars = useMemo(() => {
    const r = Number(rating) || 0;
    const full = Math.min(5, Math.max(0, Math.round(r)));
    return Array(5)
      .fill(0)
      .map((_, i) => (i < full ? "★" : "☆"));
  }, [rating]);

  const slug = useMemo(() => {
    const base = (id && String(id)) || String(title);
    return base.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }, [id, title]);

  const city = itemCity || cityProp || "";
  const region = itemRegion || itemState || regionProp || "";

  const onExplore = useCallback(() => {
    if (ctaUrl) {
      window.open(ctaUrl, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(`/areas/${slug}`, {
      state: { item, city, region },
    });
  }, [navigate, slug, item, city, region, ctaUrl]);

  // 2026-05-06 (LG feedback): when the card has a ctaUrl (ExploreMore POIs),
  // the whole card becomes clickable and the bottom button is removed —
  // a grid of 12 indigo "Open in Maps →" buttons was visually noisy. A small
  // pin/arrow overlay in the corner of the image keeps the affordance.
  // The Home.jsx case (no ctaUrl, navigates to /areas/:slug) keeps its
  // existing "Explore More" button and non-clickable card body.
  const isClickableCard = Boolean(ctaUrl);

  const s = {
    card: {
      width: fullWidth ? "100%" : undefined,
      maxWidth: fullWidth ? "100%" : 300,
      minWidth: fullWidth ? 0 : 280,
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: 16,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      overflow: "hidden",
      cursor: isClickableCard ? "pointer" : undefined,
      transition: "transform 120ms ease, box-shadow 120ms ease",
    },
    imgWrap: {
      height: 140,
      background: "#F3F4F6",
      overflow: "hidden",
      position: "relative",
    },
    img: { width: "100%", height: "100%", objectFit: "cover" },
    pinBadge: {
      position: "absolute",
      top: 10,
      right: 10,
      width: 32,
      height: 32,
      borderRadius: "50%",
      background: "rgba(17, 24, 39, 0.78)",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 14,
      pointerEvents: "none", // card itself owns the click
      backdropFilter: "blur(2px)",
    },
    body: { padding: 14 },
    title: { fontSize: 16, fontWeight: 600, marginBottom: 6, color: "#111827" },
    rating: {
      fontSize: 14,
      color: "#374151",
      marginBottom: 6,
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    subtitle: { fontSize: 14, color: "#4B5563" },
    actions: { display: "flex", gap: 8, marginTop: 10 },
    btnPrimary: {
      background: "#1559EA",
      color: "#fff",
      padding: "8px 12px",
      borderRadius: 10,
      fontSize: 12,
      border: "none",
      cursor: "pointer",
    },
  };

  // ✅ Check if we are inside ExploreMore page
  const inExploreMore = location.pathname.includes("explore");

  // Hover lift only applies when the whole card is clickable.
  const [hovered, setHovered] = useState(false);
  const cardStyle = isClickableCard && hovered
    ? { ...s.card, transform: "translateY(-2px)", boxShadow: "0 8px 20px rgba(15,23,42,0.10)" }
    : s.card;

  const cardClickHandlers = isClickableCard
    ? {
        onClick: onExplore,
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
        role: "button",
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onExplore();
          }
        },
        "aria-label": ctaText ? `${title} — ${ctaText}` : `${title} — open in maps`,
      }
    : {};

  return (
    <div style={cardStyle} {...cardClickHandlers}>
      <div style={s.imgWrap}>
        {currentSrc ? (
          <img
            // key forces React to mount a fresh <img> when the src cascades,
            // so onError fires reliably on the second URL.
            key={`stage-${imgStage}`}
            src={currentSrc}
            alt={title}
            style={s.img}
            loading="lazy"
            onError={() => setImgStage((s) => s + 1)}
          />
        ) : (
          <div
            style={{
              ...s.imgWrap,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9CA3AF",
              height: "100%",
            }}
          >
            No image
          </div>
        )}
        {isClickableCard && (
          <div style={s.pinBadge} aria-hidden="true">↗</div>
        )}
      </div>
      <div style={s.body}>
        <div style={s.title}>{title}</div>
        <div style={s.rating}>
          <span style={{ color: "#F59E0B" }}>{stars.join(" ")}</span>
          <span>{Number(rating).toFixed(1)}</span>
          <span style={{ color: "#6B7280" }}>({reviews})</span>
        </div>
        <div style={s.subtitle}>{subtitle}</div>

        {/* Bottom button only renders when the whole card isn't a click
            target — i.e., the Home.jsx "Explore More" navigation case. */}
        {!isClickableCard && (
          <div style={s.actions}>
            <button type="button" style={s.btnPrimary} onClick={onExplore}>
              Explore More
            </button>
          </div>
        )}
      </div>
    </div>
  );
}



/*
import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export default function ResultCard({
  item = {},
  city: cityProp,
  region: regionProp,
  // ✅ new: when true, card stretches to full width of its grid column
  fullWidth = false,
}) {
  const {
    id,
    title = "Downtown",
    rating = 5.0,
    reviews = "6.2k",
    subtitle = "Vibrant city center with nightlife",
    image = "",
    ctaUrl = "",
    // pass-through location hints if present on the card item
    city: itemCity,
    state: itemState,
    region: itemRegion,
  } = item || {};

  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);

  const stars = useMemo(() => {
    const r = Number(rating) || 0;
    const full = Math.min(5, Math.max(0, Math.round(r)));
    return Array(5)
      .fill(0)
      .map((_, i) => (i < full ? "★" : "☆"));
  }, [rating]);

  // Slug for the detail URL: /areas/:slug
  const slug = useMemo(() => {
    const base = (id && String(id)) || String(title);
    return base.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }, [id, title]);

  // Location hints: prefer item fields, then explicit props
  const city = itemCity || cityProp || "";
  const region = itemRegion || itemState || regionProp || "";

  const onExplore = useCallback(() => {
    navigate(`/areas/${slug}`, {
      state: {
        item,
        city,     // <— critical: send city
        region,   // <— critical: send state/region
      },
    });
  }, [navigate, slug, item, city, region]);

  const s = {
    card: {
      // ✅ stretch logic (only when fullWidth is true)
      width: fullWidth ? "100%" : undefined,
      maxWidth: fullWidth ? "100%" : 300,
      minWidth: fullWidth ? 0 : 280, // allow shrinking inside grid
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: 16,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      overflow: "hidden",
    },
    imgWrap: { height: 140, background: "#F3F4F6", overflow: "hidden" },
    img: { width: "100%", height: "100%", objectFit: "cover" },
    body: { padding: 14 },
    title: { fontSize: 16, fontWeight: 600, marginBottom: 6, color: "#111827" },
    rating: {
      fontSize: 14,
      color: "#374151",
      marginBottom: 6,
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    subtitle: { fontSize: 14, color: "#4B5563" },
    actions: { display: "flex", gap: 8, marginTop: 10 },
    btnPrimary: {
      background: "#1559EA",
      color: "#fff",
      padding: "8px 12px",
      borderRadius: 10,
      fontSize: 12,
      border: "none",
      cursor: "pointer",
    },
    linkBtn: {
      display: "inline-block",
      background: "#EEF2FF",
      color: "#1E3A8A",
      padding: "8px 12px",
      borderRadius: 10,
      fontSize: 12,
      textDecoration: "none",
      border: "1px solid #C7D2FE",
    },
  };

  return (
    <div style={s.card}>
      <div style={s.imgWrap}>
        {!imgErr && image ? (
          <img src={image} alt={title} style={s.img} loading="lazy" onError={() => setImgErr(true)} />
        ) : (
          <div
            style={{
              ...s.imgWrap,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9CA3AF",
              height: "100%",
            }}
          >
            No image
          </div>
        )}
      </div>
      <div style={s.body}>
        <div style={s.title}>{title}</div>
        <div style={s.rating}>
          <span style={{ color: "#F59E0B" }}>{stars.join(" ")}</span>
          <span>{Number(rating).toFixed(1)}</span>
          <span style={{ color: "#6B7280" }}>({reviews})</span>
        </div>
        <div style={s.subtitle}>{subtitle}</div>

        <div style={s.actions}>
          {ctaUrl && (
            <a href={ctaUrl} target="_blank" rel="noreferrer" style={s.linkBtn}>
              View details
            </a>
          )}
          <button type="button" style={s.btnPrimary} onClick={onExplore}>
            Explore More
          </button>
        </div>
      </div>
    </div>
  );
}
*/


/*
import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export default function ResultCard({ item = {}, city: cityProp, region: regionProp }) {
  const {
    id,
    title = "Downtown",
    rating = 5.0,
    reviews = "6.2k",
    subtitle = "Vibrant city center with nightlife",
    image = "",
    ctaUrl = "",
    // pass-through location hints if present on the card item
    city: itemCity,
    state: itemState,
    region: itemRegion,
  } = item || {};

  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);

  const stars = useMemo(() => {
    const r = Number(rating) || 0;
    const full = Math.min(5, Math.max(0, Math.round(r)));
    return Array(5)
      .fill(0)
      .map((_, i) => (i < full ? "★" : "☆"));
  }, [rating]);

  // Slug for the detail URL: /areas/:slug
  const slug = useMemo(() => {
    const base = (id && String(id)) || String(title);
    return base.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }, [id, title]);

  // Location hints: prefer item fields, then explicit props
  const city = itemCity || cityProp || "";
  const region = itemRegion || itemState || regionProp || "";

  const onExplore = useCallback(() => {
    navigate(`/areas/${slug}`, {
      state: {
        item,
        city,     // <— critical: send city
        region,   // <— critical: send state/region
      },
    });
  }, [navigate, slug, item, city, region]);

  const s = {
    card: {
      minWidth: 280,
      maxWidth: 300,
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: 16,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      overflow: "hidden",
    },
    imgWrap: { height: 140, background: "#F3F4F6", overflow: "hidden" },
    img: { width: "100%", height: "100%", objectFit: "cover" },
    body: { padding: 14 },
    title: { fontSize: 16, fontWeight: 600, marginBottom: 6, color: "#111827" },
    rating: {
      fontSize: 14,
      color: "#374151",
      marginBottom: 6,
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    subtitle: { fontSize: 14, color: "#4B5563" },
    actions: { display: "flex", gap: 8, marginTop: 10 },
    btnPrimary: {
      background: "#1559EA",
      color: "#fff",
      padding: "8px 12px",
      borderRadius: 10,
      fontSize: 12,
      border: "none",
      cursor: "pointer",
    },
    linkBtn: {
      display: "inline-block",
      background: "#EEF2FF",
      color: "#1E3A8A",
      padding: "8px 12px",
      borderRadius: 10,
      fontSize: 12,
      textDecoration: "none",
      border: "1px solid #C7D2FE",
    },
  };

  return (
    <div style={s.card}>
      <div style={s.imgWrap}>
        {!imgErr && image ? (
          <img src={image} alt={title} style={s.img} loading="lazy" onError={() => setImgErr(true)} />
        ) : (
          <div
            style={{
              ...s.imgWrap,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9CA3AF",
              height: "100%",
            }}
          >
            No image
          </div>
        )}
      </div>
      <div style={s.body}>
        <div style={s.title}>{title}</div>
        <div style={s.rating}>
          <span style={{ color: "#F59E0B" }}>{stars.join(" ")}</span>
          <span>{Number(rating).toFixed(1)}</span>
          <span style={{ color: "#6B7280" }}>({reviews})</span>
        </div>
        <div style={s.subtitle}>{subtitle}</div>

        <div style={s.actions}>
          {ctaUrl && (
            <a href={ctaUrl} target="_blank" rel="noreferrer" style={s.linkBtn}>
              View details
            </a>
          )}
          <button type="button" style={s.btnPrimary} onClick={onExplore}>
            Explore More
          </button>
        </div>
      </div>
    </div>
  );
}
*/



/*
import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export default function ResultCard({ item = {}, full = false }) {
  const {
    id,
    title = "Downtown",
    rating = 5.0,
    reviews = "6.2k",
    subtitle = "Vibrant city center with nightlife",
    image = "",
    ctaUrl = "",
  } = item || {};

  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);

  const stars = useMemo(() => {
    const r = Number(rating) || 0;
    const fullStars = Math.min(5, Math.max(0, Math.round(r)));
    return Array(5)
      .fill(0)
      .map((_, i) => (i < fullStars ? "★" : "☆"));
  }, [rating]);

  // Slug for the detail URL: /areas/:slug
  const slug = useMemo(() => {
    const base = (id && String(id)) || String(title);
    return base.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }, [id, title]);

  const onExplore = useCallback(() => {
    navigate(`/areas/${slug}`, { state: { item } });
  }, [navigate, slug, item]);

  const s = {
    card: {
      // If full=true, make the card fill the container (fixes right empty space on ExploreMore)
      ...(full
        ? { width: "100%" }
        : { minWidth: 280, maxWidth: 300 }),
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: 16,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      overflow: "hidden",
      boxSizing: "border-box",
    },
    imgWrap: { height: 140, background: "#F3F4F6", overflow: "hidden" },
    img: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
    body: { padding: 14 },
    title: { fontSize: 16, fontWeight: 600, marginBottom: 6, color: "#111827" },
    rating: {
      fontSize: 14,
      color: "#374151",
      marginBottom: 6,
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    subtitle: { fontSize: 14, color: "#4B5563" },
    actions: { display: "flex", gap: 8, marginTop: 10 },
    btnPrimary: {
      background: "#1559EA",
      color: "#fff",
      padding: "8px 12px",
      borderRadius: 10,
      fontSize: 12,
      border: "none",
      cursor: "pointer",
    },
    linkBtn: {
      display: "inline-block",
      background: "#EEF2FF",
      color: "#1E3A8A",
      padding: "8px 12px",
      borderRadius: 10,
      fontSize: 12,
      textDecoration: "none",
      border: "1px solid #C7D2FE",
    },
  };

  return (
    <div style={s.card}>
      <div style={s.imgWrap}>
        {!imgErr && image ? (
          <img src={image} alt={title} style={s.img} loading="lazy" onError={() => setImgErr(true)} />
        ) : (
          <div
            style={{
              ...s.imgWrap,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9CA3AF",
              height: "100%",
            }}
          >
            No image
          </div>
        )}
      </div>
      <div style={s.body}>
        <div style={s.title}>{title}</div>
        <div style={s.rating}>
          <span style={{ color: "#F59E0B" }}>{stars.join(" ")}</span>
          <span>{Number(rating).toFixed(1)}</span>
          <span style={{ color: "#6B7280" }}>({reviews})</span>
        </div>
        <div style={s.subtitle}>{subtitle}</div>

        <div style={s.actions}>
          {ctaUrl && (
            <a href={ctaUrl} target="_blank" rel="noreferrer" style={s.linkBtn}>
              View details
            </a>
          )}
          <button type="button" style={s.btnPrimary} onClick={onExplore}>
            Explore More
          </button>
        </div>
      </div>
    </div>
  );
}
*/





/*
import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export default function ResultCard({ item = {} }) {
  const {
    id,
    title = "Downtown",
    rating = 5.0,
    reviews = "6.2k",
    subtitle = "Vibrant city center with nightlife",
    image = "",
    ctaUrl = "",
  } = item || {};

  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);

  const stars = useMemo(() => {
    const r = Number(rating) || 0;
    const full = Math.min(5, Math.max(0, Math.round(r)));
    return Array(5)
      .fill(0)
      .map((_, i) => (i < full ? "★" : "☆"));
  }, [rating]);

  // Slug for the detail URL: /areas/:slug
  const slug = useMemo(() => {
    const base = (id && String(id)) || String(title);
    return base.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }, [id, title]);

  const onExplore = useCallback(() => {
    navigate(`/areas/${slug}`, { state: { item } });
  }, [navigate, slug, item]);

  const s = {
    card: {
      minWidth: 280,
      maxWidth: 300,
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: 16,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      overflow: "hidden",
    },
    imgWrap: { height: 140, background: "#F3F4F6", overflow: "hidden" },
    img: { width: "100%", height: "100%", objectFit: "cover" },
    body: { padding: 14 },
    title: { fontSize: 16, fontWeight: 600, marginBottom: 6, color: "#111827" },
    rating: { fontSize: 14, color: "#374151", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 },
    subtitle: { fontSize: 14, color: "#4B5563" },
    actions: { display: "flex", gap: 8, marginTop: 10 },
    btnPrimary: {
      background: "#1559EA",
      color: "#fff",
      padding: "8px 12px",
      borderRadius: 10,
      fontSize: 12,
      border: "none",
      cursor: "pointer",
    },
    linkBtn: {
      display: "inline-block",
      background: "#EEF2FF",
      color: "#1E3A8A",
      padding: "8px 12px",
      borderRadius: 10,
      fontSize: 12,
      textDecoration: "none",
      border: "1px solid #C7D2FE",
    },
  };

  return (
    <div style={s.card}>
      <div style={s.imgWrap}>
        {!imgErr && image ? (
          <img src={image} alt={title} style={s.img} loading="lazy" onError={() => setImgErr(true)} />
        ) : (
          <div
            style={{
              ...s.imgWrap,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9CA3AF",
              height: "100%",
            }}
          >
            No image
          </div>
        )}
      </div>
      <div style={s.body}>
        <div style={s.title}>{title}</div>
        <div style={s.rating}>
          <span style={{ color: "#F59E0B" }}>{stars.join(" ")}</span>
          <span>{Number(rating).toFixed(1)}</span>
          <span style={{ color: "#6B7280" }}>({reviews})</span>
        </div>
        <div style={s.subtitle}>{subtitle}</div>

        <div style={s.actions}>
          {ctaUrl && (
            <a href={ctaUrl} target="_blank" rel="noreferrer" style={s.linkBtn}>
              View details
            </a>
          )}
          <button type="button" style={s.btnPrimary} onClick={onExplore}>
            Explore More
          </button>
        </div>
      </div>
    </div>
  );
}
*/



/*
import React, { useState, useMemo } from "react";

export default function ResultCard({ item = {} }) {
  const {
    title = "Downtown",
    rating = 5.0,
    reviews = "6.2k",
    subtitle = "Vibrant city center with nightlife",
    image = "",
    ctaUrl = "",
  } = item;

  const [imgErr, setImgErr] = useState(false);

  const stars = useMemo(() => {
    const r = Number(rating) || 0;
    const full = Math.min(5, Math.max(0, Math.round(r)));
    return Array(5)
      .fill(0)
      .map((_, i) => (i < full ? "★" : "☆"));
  }, [rating]);

  const s = {
    card: {
      minWidth: 280,
      maxWidth: 300,
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: 16,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      overflow: "hidden",
    },
    imgWrap: { height: 140, background: "#F3F4F6", overflow: "hidden" },
    img: { width: "100%", height: "100%", objectFit: "cover" },
    body: { padding: 14 },
    title: { fontSize: 16, fontWeight: 600, marginBottom: 6, color: "#111827" },
    rating: { fontSize: 14, color: "#374151", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 },
    subtitle: { fontSize: 14, color: "#4B5563" },
    btn: {
      marginTop: 10,
      display: "inline-block",
      background: "#1559EA",
      color: "#fff",
      padding: "8px 12px",
      borderRadius: 10,
      fontSize: 12,
      textDecoration: "none",
    },
  };

  return (
    <div style={s.card}>
      <div style={s.imgWrap}>
        {!imgErr && image ? (
          <img src={image} alt={title} style={s.img} loading="lazy" onError={() => setImgErr(true)} />
        ) : (
          <div style={{ ...s.imgWrap, display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF", height: "100%" }}>
            No image
          </div>
        )}
      </div>
      <div style={s.body}>
        <div style={s.title}>{title}</div>
        <div style={s.rating}>
          <span style={{ color: "#F59E0B" }}>{stars.join(" ")}</span>
          <span>{Number(rating).toFixed(1)}</span>
          <span style={{ color: "#6B7280" }}>({reviews})</span>
        </div>
        <div style={s.subtitle}>{subtitle}</div>
        {ctaUrl && (
          <a href={ctaUrl} target="_blank" rel="noreferrer" style={s.btn}>
            View details
          </a>
        )}
      </div>
    </div>
  );
}
*/